import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from '@mui/material';
import { VisibilityOffRounded } from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { fetchLeaderboard } from '../../services/GameSlice';
import { accountSelector } from '../../services/AccountSlice';
import { getUserId } from '../../identity';
import { getSocket } from '../../socket';
import { signIn } from '../../account/session';
import './leaderboard.css';

// Games against the computer, ranked: each player's best win at a level, fewest
// moves first, then the quicker game. The page is a piece of the table: the top
// five sit as a row of chips — a sequence — in the level's colour, and the rest
// of the field is listed under them. Boards for today, this week and this month
// (calendar periods in UTC) and all time; an open page updates itself.

const LEVELS = [
    { key: 'easy', label: 'Easy' },
    { key: 'medium', label: 'Medium' },
    { key: 'hard', label: 'Hard' }
];
const PERIODS = [
    { key: 'day', label: 'Today', phrase: 'today' },
    { key: 'week', label: 'This week', phrase: 'this week' },
    { key: 'month', label: 'This month', phrase: 'this month' },
    { key: 'all', label: 'All time', phrase: null }
];

const clock = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = String(seconds % 60).padStart(2, '0');
    return m >= 60 ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

const when = (date) => {
    const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
    if (days < 1) return 'today';
    if (days < 2) return 'yesterday';
    if (days < 30) return `${days} days ago`;
    return new Date(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const ordinal = (n) => {
    const tens = n % 100;
    if (tens >= 11 && tens <= 13) return `${n}th`;
    return `${n}${{ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th'}`;
};

const untilReset = (resetsAt, now) => {
    const minutes = Math.max(0, Math.floor((new Date(resetsAt).getTime() - now) / 60000));
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    if (days > 0) return `starts again in ${days}d ${hours}h`;
    if (hours > 0) return `starts again in ${hours}h ${minutes % 60}m`;
    return `starts again in ${Math.max(1, minutes)}m`;
};

// How the game was played, in words, when it was not the plain one-on-one.
const details = (row) => [
    row.opponents > 1 ? `against ${row.opponents} computers` : null,
    row.board === 'shuffled' ? 'shuffled board' : null
].filter(Boolean).join(', ');

const keyOf = (row) => `${row.name}|${row.moves}|${row.seconds}`;

const NoHints = () => (
    <Tooltip title="Won in a game played without highlights">
        <VisibilityOffRounded className="lbx-nohints" aria-label="Won with highlights off" />
    </Tooltip>
);

/** A segmented choice; the level one shows each level's chip colour. */
const Choice = ({ label, options, value, onChange, chips }) => (
    <div className="lbx-choice" role="radiogroup" aria-label={label}>
        {options.map(option => (
            <button
                key={option.key}
                type="button"
                role="radio"
                aria-checked={value === option.key}
                className={value === option.key ? 'on' : undefined}
                onClick={() => onChange(option.key)}
            >
                {chips && <span className={`lbx-dot ${option.key}`} aria-hidden="true" />}
                {option.label}
            </button>
        ))}
    </div>
);

/** The top five as a row of chips. Places nobody has taken stay open. */
const Sequence = ({ rows, loading, fresh }) => (
    <ol className={`lbx-sequence${rows.length >= 5 ? ' complete' : ''}${loading ? ' loading' : ''}`} aria-label="Top five">
        {[0, 1, 2, 3, 4].map(i => {
            const row = rows[i] || null;
            return (
                <li key={row ? keyOf(row) : `open-${i}`} className={[row ? '' : 'open', row && fresh(row) ? 'fresh' : '', row?.isYou ? 'you' : ''].join(' ').trim() || undefined} style={{ '--i': i }}>
                    <span className="lbx-cell">
                        <span className={`lbx-chip${i === 0 && row ? ' first' : ''}`}>
                            {row && <><strong>{row.moves}</strong><small>moves</small></>}
                        </span>
                    </span>
                    <span className="lbx-place">{ordinal(i + 1)}</span>
                    {row ? <>
                        <span className="lbx-name">{row.name}{row.isYou ? ' (you)' : ''}{row.noHints && <NoHints />}</span>
                        <span className="lbx-time">{clock(row.seconds)}{details(row) ? `, ${details(row)}` : ''}</span>
                    </> : <span className="lbx-open">{loading ? '' : 'Open'}</span>}
                </li>
            );
        })}
    </ol>
);

/** Where the viewer stands, and what it takes to move up one place. */
const YouBar = ({ board, level, onSignIn }) => {
    const levelLabel = LEVELS.find(l => l.key === level).label;
    const you = board.you;
    const ahead = board.ahead;
    let line;
    if (!you) {
        line = `You're not on this board yet. Beat the ${levelLabel} computer to get on it.`;
    } else if (you.rank === 1) {
        line = `You're first, with ${you.moves} moves in ${clock(you.seconds)}.`;
    } else {
        line = `You're ${ordinal(you.rank)}, with ${you.moves} moves in ${clock(you.seconds)}.`;
        if (ahead) {
            line += ` To pass ${ahead.name}, win in fewer than ${ahead.moves} moves, or in ${ahead.moves} faster than ${clock(ahead.seconds)}.`;
        }
    }
    return (
        <section className="lbx-you" aria-label="Your place">
            <div className="lbx-you-text">
                <p>{line}</p>
                {board.yourName && <p className="lbx-you-name">
                    You appear as {board.yourName}.
                    {!board.signedIn && <> <button type="button" className="lbx-link" onClick={onSignIn}>Sign in to use your own name</button></>}
                </p>}
            </div>
            <Link to="/" className="lbx-play">Play {levelLabel}</Link>
        </section>
    );
};

const Leaderboard = () => {
    const dispatch = useDispatch();
    const { status } = useSelector(accountSelector);
    const [params, setParams] = useSearchParams();
    const level = LEVELS.some(l => l.key === params.get('level')) ? params.get('level') : 'medium';
    const period = PERIODS.some(p => p.key === params.get('period')) ? params.get('period') : 'week';
    const [board, setBoard] = useState(null);
    const [failed, setFailed] = useState(false);
    const [fresh, setFresh] = useState(new Set());
    const [now, setNow] = useState(Date.now());
    const shown = useRef(null);

    const choose = (next) => setParams({ level, period, ...next }, { replace: true });

    // A failed load is tried again a few times before it is shown: the first
    // connection to the game server can take a while.
    const load = useCallback((quietly, attempt = 0) => fetchLeaderboard(level, getUserId(), period)
        .then(data => {
            const before = shown.current;
            setFresh(quietly && before ? new Set(data.fastest.map(keyOf).filter(k => !before.has(k))) : new Set());
            shown.current = new Set(data.fastest.map(keyOf));
            setBoard(data);
            setFailed(false);
        })
        .catch(() => {
            if (attempt < 3) {
                return new Promise(r => setTimeout(r, 1500)).then(() => load(quietly, attempt + 1));
            }
            if (!quietly) {
                setFailed(true);
            }
            return null;
        }), [level, period]);

    useEffect(() => {
        shown.current = null;
        setBoard(null);
        setFailed(false);
        load(false);
    }, [load]);

    // Names and "you" change when someone signs in or out here.
    const firstStatus = useRef(status);
    useEffect(() => {
        if (status !== firstStatus.current && status !== 'unknown') {
            firstStatus.current = status;
            load(true);
        }
    }, [status, load]);

    // Live: refresh when a result comes in at this level.
    useEffect(() => {
        const socket = getSocket();
        const watch = () => socket.emit('leaderboard:watch');
        let timer = null;
        const changed = ({ difficulty }) => {
            if (difficulty === level) {
                clearTimeout(timer);
                timer = setTimeout(() => load(true), 400);
            }
        };
        watch();
        socket.on('connect', watch);
        socket.on('leaderboard:changed', changed);
        return () => {
            clearTimeout(timer);
            socket.off('connect', watch);
            socket.off('leaderboard:changed', changed);
            socket.emit('leaderboard:unwatch');
        };
    }, [level, load]);

    useEffect(() => {
        const tick = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(tick);
    }, []);
    useEffect(() => {
        if (board?.resetsAt && new Date(board.resetsAt).getTime() <= now) {
            load(true);
        }
    }, [board, now, load]);
    useEffect(() => {
        if (fresh.size === 0) {
            return undefined;
        }
        const timer = setTimeout(() => setFresh(new Set()), 4000);
        return () => clearTimeout(timer);
    }, [fresh]);

    const levelLabel = LEVELS.find(l => l.key === level).label;
    const periodInfo = PERIODS.find(p => p.key === period);
    const rest = board ? board.fastest.slice(5) : [];
    const youOutside = Boolean(board?.you) && !board.fastest.some(row => row.isYou);
    const isFresh = (row) => fresh.has(keyOf(row));
    const nobodyYet = periodInfo.phrase ? `Nobody has beaten the ${levelLabel} computer ${periodInfo.phrase} yet.` : `Nobody has beaten the ${levelLabel} computer yet.`;

    return (
        <div className={`lbx ${level}`}>
            <Helmet>
                <title>Leaderboard - Fastest Wins Against the Computer | Play Sequence</title>
                <meta name="description" content="The fastest wins against the computer on Play Sequence, at Easy, Medium and Hard: today, this week, this month and of all time." />
            </Helmet>

            <div className="lbx-inner">
                <header className="lbx-head">
                    <h1>Leaderboard</h1>
                    <p>The quickest wins against the computer. Fewest moves wins, and the faster game breaks a tie. Each player's best win counts once.</p>
                </header>

                <div className="lbx-controls">
                    <Choice label="Computer level" options={LEVELS} value={level} onChange={(key) => choose({ level: key })} chips />
                    <Choice label="Period" options={PERIODS} value={period} onChange={(key) => choose({ period: key })} />
                    <p className="lbx-status">
                        <span className="lbx-live" aria-hidden="true" />
                        {board?.resetsAt ? `Live, ${untilReset(board.resetsAt, now)}` : 'Live'}
                    </p>
                </div>

                {failed ? <div className="lbx-message">
                    <p>The leaderboard didn't load. The game server may be restarting.</p>
                    <button type="button" className="lbx-play" onClick={() => { setFailed(false); load(false); }}>Try again</button>
                </div> : <>
                    <Sequence rows={board ? board.fastest.slice(0, 5) : []} loading={!board} fresh={isFresh} />
                    {board && board.fastest.length === 0 && <p className="lbx-empty">{nobodyYet} Win one and the first chip is yours.</p>}
                    {board && <YouBar board={board} level={level} onSignIn={() => signIn(dispatch)} />}

                    <div className="lbx-columns">
                        <section className="lbx-field" aria-labelledby="lbx-field-title">
                            <h2 id="lbx-field-title">Everyone else</h2>
                            {!board && <ol className="lbx-rows loading" aria-hidden="true">{[0, 1, 2, 3, 4].map(i => <li key={i}><span /></li>)}</ol>}
                            {board && rest.length === 0 && !youOutside && <p className="lbx-quiet">
                                {board.fastest.length > 0 ? 'Nobody else yet: everyone with a win is in the top five.' : 'Nobody yet.'}
                            </p>}
                            {board && (rest.length > 0 || youOutside) && <ol className="lbx-rows">
                                <li className="lbx-rows-head" aria-hidden="true"><span>Place</span><span>Player</span><span>Moves</span><span>Time</span></li>
                                {rest.map(row => (
                                    <li key={keyOf(row)} className={[row.isYou ? 'you' : '', isFresh(row) ? 'fresh' : ''].join(' ').trim() || undefined}>
                                        <span className="lbx-rank">{row.rank}</span>
                                        <span className="lbx-who">
                                            <span className="lbx-name">{row.name}{row.isYou ? ' (you)' : ''}{row.noHints && <NoHints />}</span>
                                            <span className="lbx-meta">{when(row.finishedAt)}{details(row) ? `, ${details(row)}` : ''}</span>
                                        </span>
                                        <span className="lbx-moves">{row.moves}</span>
                                        <span className="lbx-clock">{clock(row.seconds)}</span>
                                    </li>
                                ))}
                                {youOutside && <li className="you apart">
                                    <span className="lbx-rank">{board.you.rank}</span>
                                    <span className="lbx-who">
                                        <span className="lbx-name">{board.you.name} (you)</span>
                                        <span className="lbx-meta">{when(board.you.finishedAt)}</span>
                                    </span>
                                    <span className="lbx-moves">{board.you.moves}</span>
                                    <span className="lbx-clock">{clock(board.you.seconds)}</span>
                                </li>}
                            </ol>}
                        </section>

                        <aside className="lbx-side" aria-labelledby="lbx-wins-title">
                            <h2 id="lbx-wins-title">Most wins</h2>
                            {board && board.players.length === 0 && <p className="lbx-quiet">No wins {periodInfo.phrase || 'yet'}.</p>}
                            {board && board.players.length > 0 && <ol className="lbx-wins">
                                {board.players.slice(0, 10).map(row => (
                                    <li key={`${row.name}-${row.rank}`} className={row.isYou ? 'you' : undefined}>
                                        <span className="lbx-rank">{row.rank}</span>
                                        <span className="lbx-name">{row.name}{row.isYou ? ' (you)' : ''}</span>
                                        <span className="lbx-count">{row.wins} {row.wins === 1 ? 'win' : 'wins'}<small> of {row.games}</small></span>
                                    </li>
                                ))}
                            </ol>}
                            <p className="lbx-note">
                                Only games of one player against the computer count. Players who haven't signed in appear under a name made up for their browser; the name typed into a game is never shown here.
                            </p>
                            <p className="lbx-note">Today, this week and this month start again at midnight UTC, on Mondays and on the 1st.</p>
                        </aside>
                    </div>
                </>}
            </div>
        </div>
    );
};

export default Leaderboard;
